import React from 'react'
import * as QueryString from 'query-string'
import { useLocation, useParams } from 'react-router'
import { RaceNavigation } from '@core/Areas/Racing/Components/Navigation/RaceNavigation'
import { isThoroughbredRacePageInWA } from '@core/Areas/Racing/helpers/liveVideoFeedHelpers'
import { DockedPlayer as DockedSkyVideoPlayer } from '@core/Areas/SkyVideoPlayer/Components/DockedPlayer'
import { MainContainer } from '@core/Components/Containers/MainContainer'
import { keys } from '@core/Services/Analytics/keys'
import { trackKey } from '@core/Services/Analytics/track'
import { useAppSeoForRacing } from '@core/Utils/hooks/useAppSeo'
import { useRacePageData } from '../Racing/Hooks'
import { setSelectedRace } from '../Racing/helpers'
import { RaceCardContent } from './Components/RaceCardContent'
import { RaceSwipeContainer } from './Components/RaceSwipeContainer'
import type { Direction } from './Components/RaceSwipeContainer/types'
import { useDisableAndroidBackGesture } from './Hooks/useDisableAndroidBackGesture'

interface Params extends Record<string, string> {
  meetingId: string
  raceNumber: string
}

export const RaceCard = React.memo(() => {
  useDisableAndroidBackGesture()

  const { meetingId, raceNumber } = useParams<Params>() as Params
  const { date: meetingDate } = QueryString.parse(useLocation().search) as { date: string }

  const { race } = useRacePageData({ meetingDate, meetingId, raceNumber })
  useAppSeoForRacing({ page: 'race-card', data: { race } })

  React.useEffect(() => {
    const delay = setTimeout(() => {
      trackKey(keys.visitedRacePage)
    }, 500)
    return () => clearTimeout(delay)
  }, [])

  const shouldDisplayRacingWA = isThoroughbredRacePageInWA(race)

  const races = race?.MeetingInformation.Races ?? []

  const currentRace = races?.find(race => race.RaceNumber.toString() === raceNumber)
  const lastRaceNumber = races.length > 0 ? races[races.length - 1].RaceNumber : null
  const firstRaceNumber = races.length > 0 ? races[0].RaceNumber : null

  const prevRace = currentRace && races.find(race => race.RaceNumber === currentRace.RaceNumber - 1)
  const nextRace = currentRace && races.find(race => race.RaceNumber === currentRace.RaceNumber + 1)

  return (
    <MainContainer background='white'>
      <DockedSkyVideoPlayer />

      <RaceNavigation
        meetingDate={meetingDate}
        raceNumber={+raceNumber}
        meetingId={meetingId}
        isRacingWA={shouldDisplayRacingWA}
      />

      <RaceSwipeContainer
        onSwipeCallback={(direction: Direction) => {
          const raceToSwipeTo = direction === 'BACKWARD' ? prevRace : nextRace
          if (raceToSwipeTo) {
            setSelectedRace(+raceToSwipeTo.RaceNumber)
          }
        }}
        isLastRace={!currentRace || lastRaceNumber === currentRace.RaceNumber}
        isFirstRace={!currentRace || firstRaceNumber === currentRace.RaceNumber}
      >
        <RaceCardContent meetingDate={meetingDate} meetingId={meetingId} raceNumber={raceNumber} />
      </RaceSwipeContainer>
    </MainContainer>
  )
})

RaceCard.displayName = 'RaceCard'
